import { cn } from 'lib/utils'
import { Check, ChevronDown } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { Tooltip } from './tooltip'

export interface HoverSelectOption {
  label: string | ReactNode
  value: string
  selectedLabel?: string | ReactNode
}

interface HoverSelectProps {
  options: HoverSelectOption[]
  placeholder?: string // Placeholder text
  value?: string // Selected value
  onChange: (value: string) => void // Callback when value changes
  popoverClassName?: string // Custom className for PopoverContent
  align?: 'start' | 'center' | 'end' // Alignment of the popover
  tooltip?: string // Optional tooltip for the trigger
}
export const HoverSelect: React.FC<HoverSelectProps> = ({
  options,
  placeholder = 'Select',
  value,
  onChange,
  popoverClassName = 'max-w-36 p-0',
  align = 'center',
  tooltip,
}) => {
  const selectedOption = options.find((option) => option.value === value)
  const [open, setOpen] = useState(false)

  const trigger = (
    <PopoverTrigger asChild>
      <div
        className={cn(
          'cursor-pointer flex place-items-center text-tertiary',
          open && 'text-primary',
        )}
      >
        {selectedOption && (
          <div className="flex items-center">
            {selectedOption.selectedLabel || selectedOption.label}
          </div>
        )}
        <ChevronDown size={16} />
      </div>
    </PopoverTrigger>
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {tooltip && !open ? <Tooltip content={tooltip}>{trigger}</Tooltip> : trigger}
      <PopoverContent
        className={popoverClassName}
        side="top"
        sideOffset={8}
        align={align}
        avoidCollisions={true}
      >
        <div className="font-inter text-s-500 normal-case tracking-normal px-2 py-1 text-secondary">
          {placeholder}
        </div>
        <ul className="list-none font-inter text-s-400 normal-case tracking-normal max-h-44 overflow-y-auto">
          {options.map((option) => (
            <li
              onKeyDown={() => {}}
              key={option.value}
              value={option.value}
              className={`cursor-pointer p-2 hover:bg-muted flex items-center rounded ${
                value === option.value ? 'bg-accent text-accent-foreground text-s-500' : ''
              }`}
              onClick={() => {
                onChange?.(option.value)
                setOpen(false)
              }}
            >
              <Check size={16} className={cn('mr-2', value !== option.value && 'invisible')} />
              <div className="flex-1">{option.label}</div>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}
